import React, { useState, useEffect } from 'react';
import {
  subscribeToCompanyBySlug,
  subscribeToPreparers,
  subscribeToAvailability,
  subscribeToAppointments,
  addAppointment
} from '../firestoreHelper';
import { getTemplateById } from '../landingPageTemplates';

// Sanitize corrupted UTF-8 emojis from database
const sanitizeIcon = (icon, fallbackIndex = 0) => {
  if (!icon || typeof icon !== 'string') return '⭐';
  
  const defaultIcons = ['✅', '💵', '🌐', '📞', '⭐', '📋', '🔒', '💼'];
  
  // Check for corrupted patterns - these characters appear in double-encoded UTF-8
  const corruptedChars = ['ð', 'Ÿ', 'Â', 'Ã', 'â', '€', '™', 'œ', '¢', '¨', '©', '®', '°', 
                          '\u00c3', '\u00c2', '\u00e2', '\u00f0', '\u0178', '\u0153'];
  
  // If icon contains ANY corrupted character patterns, replace it
  for (const char of corruptedChars) {
    if (icon.includes(char)) {
      return defaultIcons[fallbackIndex % defaultIcons.length];
    }
  }
  
  // If icon is longer than 4 chars, it's likely corrupted (real emojis are 1-2 chars typically)
  if (icon.length > 4) {
    return defaultIcons[fallbackIndex % defaultIcons.length];
  }
  
  return icon;
};

function CompanyLandingPage({ companySlug, onAdminLogin, onPreparerLogin, preSelectedPreparerId }) {
  const [company, setCompany] = useState(null);
  const [preparers, setPreparers] = useState([]);
  const [availability, setAvailability] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedPreparer, setSelectedPreparer] = useState(null);
  const [hasAutoSelectedPreparer, setHasAutoSelectedPreparer] = useState(false);
  const [showDatePopover, setShowDatePopover] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Login Modal State
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // PWA Install Instructions Modal State
  const [showInstallModal, setShowInstallModal] = useState(false);
  
  // Services slider auto-scroll state
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const sliderHoveredRef = React.useRef(false);

  const [formData, setFormData] = useState({
    date: '',
    time: '',
    timeSlots: [],
    fullName: '',
    email: '',
    phoneNumber: '',
    numberOfClients: '1',
    fillingType: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Get template for this company
  const template = company ? getTemplateById(company.templateId || 'taxfile-classic') : null;

  // Utility functions
  const convert24to12Hour = (time24) => {
    if (!time24 || typeof time24 !== 'string') return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${period}`;
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhoneNumber = (phone) => phone.replace(/\D/g, '').length === 10;

  // Login Handler - Preparer only
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const preparer = preparers?.find(
        p => p.username === loginData.username && p.password === loginData.password
      );
      if (preparer) {
        if (!preparer.active) {
          alert('Your account is inactive. Please contact your company admin.');
        } else {
          setShowLoginModal(false);
          // Use callback to properly set state in App.js
          if (onPreparerLogin) {
            onPreparerLogin(preparer);
          }
        }
      } else {
        alert('Invalid preparer credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed: ' + error.message);
    }
    setLoginLoading(false);
  };

  // Load company data with real-time updates
  useEffect(() => {
    let unsubCompany = null;
    let unsubPreparers = null;
    let unsubAvailability = null;
    let unsubAppointments = null;
    
    // Save company slug for PWA home screen bookmark
    // This allows users to add this company's page to their home screen
    if (companySlug) {
      localStorage.setItem('pwa_company_slug', companySlug);
      console.log('📱 Saved PWA company slug:', companySlug);
    }

    // Subscribe to company data for real-time updates
    unsubCompany = subscribeToCompanyBySlug(companySlug, (companyData) => {
      if (!companyData) {
        setError('Company not found');
        setLoading(false);
        return;
      }
      // Check both old field names (published/active) and new ones (isPublished/isActive) for compatibility
      const isPublished = companyData.isPublished || companyData.published;
      const isActive = companyData.isActive !== false && companyData.active !== false;
      if (!isPublished || !isActive) {
        setError('This page is not available');
        setLoading(false);
        return;
      }
      
      // Get company logo from multiple sources
      const companyLogo = companyData.landingPageSettings?.logoUrl || 
                         companyData.logoUrl || 
                         companyData.logo;
      
      // Update page title and favicon for this company
      document.title = `${companyData.name} - Book Appointment`;
      
      // Update all icons for PWA - favicon, apple-touch-icon, etc.
      if (companyLogo) {
        // Update favicon
        const favicon = document.getElementById('favicon');
        if (favicon) favicon.href = companyLogo;
        
        // Update existing apple-touch-icon
        const appleTouchIcon = document.getElementById('apple-touch-icon') || document.querySelector('link[rel="apple-touch-icon"]');
        if (appleTouchIcon) appleTouchIcon.href = companyLogo;
        
        // Add multiple apple-touch-icon sizes for iOS (if not already present)
        const iconSizes = ['180x180', '152x152', '144x144', '120x120', '114x114', '76x76', '72x72', '60x60', '57x57'];
        iconSizes.forEach(size => {
          const existingIcon = document.querySelector(`link[rel="apple-touch-icon"][sizes="${size}"]`);
          if (!existingIcon) {
            const newIcon = document.createElement('link');
            newIcon.rel = 'apple-touch-icon';
            newIcon.sizes = size;
            newIcon.href = companyLogo;
            document.head.appendChild(newIcon);
          } else {
            existingIcon.href = companyLogo;
          }
        });
        
        // Also update precomposed icons for older iOS
        const precomposedIcon = document.querySelector('link[rel="apple-touch-icon-precomposed"]');
        if (precomposedIcon) {
          precomposedIcon.href = companyLogo;
        } else {
          const newPrecomposed = document.createElement('link');
          newPrecomposed.rel = 'apple-touch-icon-precomposed';
          newPrecomposed.href = companyLogo;
          document.head.appendChild(newPrecomposed);
        }
      }
      
      // Update dynamic PWA manifest with company name and logo
      // This ensures proper branding when adding to home screen
      const updatePWAManifest = () => {
        const dynamicManifest = {
          "short_name": companyData.name.length > 12 ? companyData.name.substring(0, 12) : companyData.name,
          "name": `${companyData.name} - Tax Appointments`,
          "icons": [
            {
              "src": companyLogo || "/logo.png",
              "sizes": "192x192",
              "type": "image/png",
              "purpose": "any maskable"
            },
            {
              "src": companyLogo || "/logo.png",
              "sizes": "512x512",
              "type": "image/png",
              "purpose": "any maskable"
            }
          ],
          "start_url": `/company/${companySlug}?pwa=true`,
          "scope": "/",
          "display": "standalone",
          "theme_color": companyData.landingPageSettings?.primaryColor || "#0a2540",
          "background_color": "#ffffff",
          "orientation": "portrait-primary",
          "prefer_related_applications": false,
          "categories": ["business", "productivity", "finance"]
        };
        
        const manifestBlob = new Blob([JSON.stringify(dynamicManifest)], {type: 'application/json'});
        const manifestUrl = URL.createObjectURL(manifestBlob);
        
        const manifestLink = document.getElementById('pwa-manifest');
        if (manifestLink) {
          // Revoke old blob URL to prevent memory leak
          if (manifestLink.href.startsWith('blob:')) {
            URL.revokeObjectURL(manifestLink.href);
          }
          manifestLink.href = manifestUrl;
        }
        
        // Update apple-mobile-web-app-title
        const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
        if (appleTitle) {
          appleTitle.content = companyData.name;
        }
        
        // Save company data to localStorage for faster PWA icon loading on next visit
        try {
          localStorage.setItem('pwa_company_logo_' + companySlug, companyLogo || '');
          localStorage.setItem('pwa_company_name_' + companySlug, companyData.name);
          localStorage.setItem('pwa_company_color_' + companySlug, companyData.landingPageSettings?.primaryColor || '#0a2540');
        } catch(e) {
          console.log('Could not cache company data:', e);
        }
      };
      
      updatePWAManifest();
      
      // Update company data (this will trigger re-render with new services, etc.)
      setCompany(companyData);
      
      // Only set up other subscriptions once we have a valid company
      if (!unsubPreparers) {
        unsubPreparers = subscribeToPreparers((data) => {
          // Check both active and isActive fields for backwards compatibility
          const companyPreparers = data.filter(p => 
            p.companyId === companyData.id && 
            (p.active !== false && p.isActive !== false) &&
            (p.published !== false && p.isPublished !== false)
          );
          setPreparers(companyPreparers);
        }, companyData.id);

        unsubAvailability = subscribeToAvailability((data) => {
          setAvailability(data);
        }, companyData.id);

        unsubAppointments = subscribeToAppointments((data) => {
          const companyAppointments = data.filter(a => a.companyId === companyData.id);
          setAppointments(companyAppointments);
        }, companyData.id);
      }
      
      setLoading(false);
    });

    return () => {
      // Reset favicon and title when leaving company page
      document.title = 'TaxFilePro - Appointment Booking';
      const favicon = document.getElementById('favicon');
      if (favicon) {
        favicon.href = '/logo.png';
      }
      if (unsubCompany) unsubCompany();
      if (unsubPreparers) unsubPreparers();
      if (unsubAvailability) unsubAvailability();
      if (unsubAppointments) unsubAppointments();
    };
  }, [companySlug]);

  // Auto-select preparer if preSelectedPreparerId is provided (for direct booking links)
  // Supports both name-based slugs (john-smith) and legacy ID-based links
  useEffect(() => {
    if (preSelectedPreparerId && preparers.length > 0 && !hasAutoSelectedPreparer) {
      // Create slug from name for matching
      const createSlug = (name) => name.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      
      // Try to match by name slug first (new format), then by ID (legacy)
      const preparer = preparers.find(p => createSlug(p.name) === preSelectedPreparerId) 
                    || preparers.find(p => p.id === preSelectedPreparerId);
      
      if (preparer) {
        setSelectedPreparer(preparer);
        setShowBookingForm(true);
        setHasAutoSelectedPreparer(true);
        // Update page title to include preparer name
        document.title = `Book with ${preparer.name} - ${company?.name || 'Book Appointment'}`;
      }
    }
  }, [preSelectedPreparerId, preparers, hasAutoSelectedPreparer, company?.name]);



  // Auto-slide services every 5 seconds (pauses on hover)
  useEffect(() => {
    const servicesList = company?.servicesList || company?.landingPageSettings?.services || [];
    if (servicesList.length <= 1) return;
    const interval = setInterval(() => {
      if (sliderHoveredRef.current) return;
      setCurrentServiceIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % servicesList.length;
        const container = document.getElementById('services-slider');
        if (container) {
          const firstCard = container.querySelector(':scope > div');
          const gap = parseFloat(getComputedStyle(container).gap) || 20;
          const cardWidth = firstCard ? firstCard.offsetWidth + gap : 300;
          container.scrollTo({ left: nextIndex * cardWidth, behavior: 'smooth' });
        }
        return nextIndex;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [company]);

  const getAvailableTimeSlots = (selectedDate, preparerName) => {
    if (!selectedDate || !preparerName) return [];
    const key = `${preparerName}_${selectedDate}`;
    const availableSlots = availability[key] || [];
    // Hide booked time slots for active appointments (pending/confirmed)
    // Cancelled/completed/deleted appointments free up the slot so client can rebook
    const bookedSlots = [];
    appointments
      .filter(apt => apt.preparer === preparerName && apt.status !== 'cancelled' && apt.status !== 'completed')
      .forEach(apt => {
        if (apt.timeSlots && Array.isArray(apt.timeSlots)) {
          apt.timeSlots.forEach(s => {
            if (s.date === selectedDate) bookedSlots.push(s.time);
          });
        } else if (apt.date === selectedDate && apt.time) {
          bookedSlots.push(apt.time);
        }
      });
    return availableSlots.filter(slot => !bookedSlots.includes(slot));
  };

  const getAvailableDates = (preparerName) => {
    if (!preparerName) return [];
    return Object.keys(availability)
      .filter(key => key.startsWith(preparerName + '_'))
      .map(key => key.split('_')[1])
      .sort();
  };

  const isDateFullyBooked = (date, preparerName) => {
    const availableSlots = getAvailableTimeSlots(date, preparerName);
    return availableSlots.length === 0 && availability[`${preparerName}_${date}`]?.length > 0;
  };

  // Generate shareable booking link for a preparer using name slug
  const createPreparerSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Remove multiple hyphens
      .trim();
  };

  const getPreparerBookingLink = (preparer) => {
    const nameSlug = createPreparerSlug(preparer.name);
    return `${window.location.origin}/company/${companySlug}/book/${nameSlug}`;
  };

  // Copy preparer booking link to clipboard
  const handleCopyPreparerLink = async (e, preparer) => {
    e.stopPropagation(); // Prevent card click
    const link = getPreparerBookingLink(preparer);
    try {
      await navigator.clipboard.writeText(link);
      alert(`✅ Booking link copied!\n\nShare this link for clients to book directly with ${preparer.name}:\n\n${link}`);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert(`✅ Booking link copied!\n\nShare this link for clients to book directly with ${preparer.name}:\n\n${link}`);
    }
  };

  const handleSelectPreparer = (preparer) => {
    setSelectedPreparer(preparer);
    setFormData(prev => ({ ...prev, date: '', time: '', timeSlots: [] }));
    setShowBookingForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phoneNumber') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else if (name === 'numberOfClients') {
      setFormData(prev => ({ ...prev, [name]: value, time: '', date: '', timeSlots: [] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Multi-client time slot toggle handler
  const handleTimeSlotToggle = (date, time) => {
    const numClients = parseInt(formData.numberOfClients) || 1;
    setFormData(prev => {
      const existing = prev.timeSlots || [];
      const slotIndex = existing.findIndex(s => s.date === date && s.time === time);
      if (slotIndex >= 0) {
        // Remove this slot
        const updated = existing.filter((_, i) => i !== slotIndex);
        return { ...prev, timeSlots: updated, time: updated.length > 0 ? updated[0].time : '', date: updated.length > 0 ? updated[0].date : prev.date };
      } else if (existing.length < numClients) {
        // Add this slot
        const updated = [...existing, { date, time }].sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
        return { ...prev, timeSlots: updated, time: updated[0].time, date: prev.date || date };
      } else {
        // Already at max - if single client, just replace
        if (numClients === 1) {
          return { ...prev, timeSlots: [{ date, time }], time: time, date: date };
        }
        return prev; // at max for multi-client
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    
    const numClients = parseInt(formData.numberOfClients) || 1;
    
    // Validation with clear error messages
    if (!formData.fullName?.trim()) {
      alert('Please enter your full name');
      return;
    }
    if (!formData.email?.trim()) {
      alert('Please enter your email address');
      return;
    }
    if (!validateEmail(formData.email)) {
      alert('Please enter a valid email address');
      return;
    }
    if (!formData.phoneNumber?.trim()) {
      alert('Please enter your phone number');
      return;
    }
    if (!validatePhoneNumber(formData.phoneNumber)) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }
    if (!formData.fillingType) {
      alert('Please select a service type');
      return;
    }
    // Multi-client validation
    const slots = formData.timeSlots || [];
    if (slots.length < numClients) {
      alert(`Please select ${numClients} time slot(s). You have selected ${slots.length}.`);
      return;
    }

    // Check if all slots are still available (real-time check)
    for (const slot of slots) {
      const availableSlots = getAvailableTimeSlots(slot.date, selectedPreparer?.name);
      if (!availableSlots.includes(slot.time)) {
        alert(`⚠️ Sorry, the time slot ${convert24to12Hour(slot.time)} on ${new Date(slot.date + 'T00:00:00').toLocaleDateString()} was just booked by someone else.\n\nPlease select a different time.`);
        setFormData(prev => ({ ...prev, timeSlots: prev.timeSlots.filter(s => !(s.date === slot.date && s.time === slot.time)) }));
        return;
      }
    }

    setSubmitting(true);
    try {
      // Group slots by date to decide save strategy
      const slotsByDate = {};
      slots.forEach(s => {
        if (!slotsByDate[s.date]) slotsByDate[s.date] = [];
        slotsByDate[s.date].push(s);
      });
      const uniqueDates = Object.keys(slotsByDate);

      const baseData = {
        ...formData,
        fullName: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        preparer: selectedPreparer.name,
        preparerId: selectedPreparer.id,
        companyId: company.id,
        companyName: company.name,
        confirmed: false,
        createdAt: new Date().toISOString()
      };

      if (uniqueDates.length === 1) {
        // All same date -> ONE appointment document
        await addAppointment({
          ...baseData,
          numberOfClients: numClients.toString(),
          date: slots[0].date,
          time: slots[0].time,
          timeSlots: slots
        });
      } else {
        // Different dates -> SEPARATE documents per unique date
        const groupId = `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        for (const date of uniqueDates) {
          const dateSlots = slotsByDate[date];
          await addAppointment({
            ...baseData,
            numberOfClients: dateSlots.length.toString(),
            date: date,
            time: dateSlots[0].time,
            timeSlots: dateSlots,
            groupId: groupId,
            totalGroupClients: numClients.toString()
          });
        }
      }

      setFormData({
        date: '', time: '', timeSlots: [], fullName: '',
        email: '', phoneNumber: '', numberOfClients: '1', fillingType: ''
      });
      setShowBookingForm(false);
      setSelectedPreparer(null);
      alert('✅ Appointment Successfully Booked!\n\n📧 A confirmation email will be sent once confirmed.');
    } catch (error) {
      console.error('Error booking:', error);
      // Better error messages based on error type
      if (error.code === 'unavailable' || error.message?.includes('network')) {
        alert('📶 Network error. Please check your internet connection and try again.');
      } else if (error.code === 'permission-denied') {
        alert('⚠️ Booking failed. Please refresh the page and try again.');
      } else {
        alert('❌ Error booking appointment. Please try again.\n\nIf the problem persists, please contact support.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="smarttax-loading">
        <div className="smarttax-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="smarttax-error">
        <div className="error-icon">❌</div>
        <h1>{error}</h1>
        <p>The page you're looking for doesn't exist or is not available.</p>
      </div>
    );
  }

  // Get customizable colors from company settings or template
  // Default: Friend Tax Services green/teal theme
  const primaryColor = company.primaryColor || template?.primaryColor || '#2e7d32';
  const secondaryColor = company.secondaryColor || template?.secondaryColor || '#00695c';
  const accentColor = company.accentColor || template?.accentColor || '#4caf50';
  const gradientStart = template?.gradientStart || primaryColor;
  const gradientEnd = template?.gradientEnd || secondaryColor;
  
  // Template style settings
  const heroStyle = template?.heroStyle || 'gradient';
  const cardStyle = template?.cardStyle || 'shadow';
  const buttonStyle = template?.buttonStyle || 'solid';
  const layout = template?.layout || 'classic';
  
  // Section visibility - show more sections by default with mock data
  const sections = company.sections || {
    hero: true, about: true, services: true, preparers: true,
    features: true, testimonials: true, stats: true, contact: true, cta: true
  };

  // CSS Variables for dynamic theming
  const themeStyles = {
    '--st-primary': primaryColor,
    '--st-secondary': secondaryColor,
    '--st-accent': accentColor,
    '--st-gradient': `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
    '--st-gradient-hover': `linear-gradient(135deg, ${gradientEnd} 0%, ${gradientStart} 100%)`,
    '--st-gradient-start': gradientStart,
    '--st-gradient-end': gradientEnd,
  };

  // Template class names for different styles
  const templateClasses = `template-${template?.id || 'taxfile-classic'} hero-${heroStyle} card-${cardStyle} btn-${buttonStyle} layout-${layout}`;

  // Check if running as PWA
  const isPWAMode = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

  return (
    <div className={`smarttax-landing ${templateClasses}`} style={themeStyles}>
      
      {/* ========== MODERN NAVBAR ========== */}
      <nav className="st-navbar" style={{
        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
        backdropFilter: 'blur(20px)',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
        borderBottom: 'none',
        // PWA mode: add extra padding for iOS safe area
        paddingTop: isPWAMode ? 'max(44px, env(safe-area-inset-top, 44px))' : undefined
      }}>
        <div className="st-container">
          <div className="st-nav-inner" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 0',
            gap: 'clamp(16px, 3vw, 40px)'
          }}>
            {/* Logo */}
            <a 
              href="#home" 
              className="st-logo"
              onClick={(e) => {
                e.preventDefault();
                setShowBookingForm(false);
                setSelectedPreparer(null);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                textDecoration: 'none',
                transition: 'all 0.3s ease'
              }}
            >
              {(company.landingPageSettings?.logoUrl || company.logoUrl || company.logo) ? (
                <img src={company.landingPageSettings?.logoUrl || company.logoUrl || company.logo} alt={company.name} style={{
                  height: '60px',
                  width: 'auto',
                  maxWidth: 'min(220px, 40vw)',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }} />
              ) : (
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 4px 15px ${primaryColor}40`
                }}>📊</div>
              )}
              <span style={{
                fontSize: 'clamp(1rem, 3vw, 1.4rem)',
                fontWeight: '800',
                color: 'white',
                textShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}>{company.name}</span>
            </a>

            {/* Desktop Navigation */}
            <div className="st-nav-links" style={{
              alignItems: 'center',
              gap: '8px'
            }}>
              {sections.about && (
                <a href="#about" style={{
                  padding: '10px 18px',
                  color: 'rgba(255,255,255,0.9)',
                  textDecoration: 'none',
                  fontWeight: '500',
                  fontSize: '0.95rem',
                  borderRadius: '10px',
                  transition: 'all 0.3s ease'
                }} onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.15)';
                  e.target.style.color = 'white';
                }} onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgba(255,255,255,0.9)';
                }}>About</a>
              )}
              {sections.services && (
                <a href="#services" style={{
                  padding: '10px 18px',
                  color: 'rgba(255,255,255,0.9)',
                  textDecoration: 'none',
                  fontWeight: '500',
                  fontSize: '0.95rem',
                  borderRadius: '10px',
                  transition: 'all 0.3s ease'
                }} onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.15)';
                  e.target.style.color = 'white';
                }} onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgba(255,255,255,0.9)';
                }}>Services</a>
              )}
              <a href="#features" style={{
                padding: '10px 18px',
                color: 'rgba(255,255,255,0.9)',
                textDecoration: 'none',
                fontWeight: '500',
                fontSize: '0.95rem',
                borderRadius: '10px',
                transition: 'all 0.3s ease'
              }} onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.15)';
                e.target.style.color = 'white';
              }} onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = 'rgba(255,255,255,0.9)';
              }}>Why Us</a>
              <a href="#team" style={{
                padding: '10px 18px',
                color: 'rgba(255,255,255,0.9)',
                textDecoration: 'none',
                fontWeight: '500',
                fontSize: '0.95rem',
                borderRadius: '10px',
                transition: 'all 0.3s ease'
              }} onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.15)';
                e.target.style.color = 'white';
              }} onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = 'rgba(255,255,255,0.9)';
              }}>Our Team</a>
              {sections.contact && (
                <a href="#contact" style={{
                  padding: '10px 18px',
                  color: 'rgba(255,255,255,0.9)',
                  textDecoration: 'none',
                  fontWeight: '500',
                  fontSize: '0.95rem',
                  borderRadius: '10px',
                  transition: 'all 0.3s ease'
                }} onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255,255,255,0.15)';
                  e.target.style.color = 'white';
                }} onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'rgba(255,255,255,0.9)';
                }}>Contact</a>
              )}
            </div>

            {/* Nav Actions */}
            <div className="st-nav-actions" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              {/* Social Links */}
              {company.socialLinks && (
                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  {company.socialLinks.facebook && (
                    <a href={company.socialLinks.facebook} target="_blank" rel="noopener noreferrer" style={{
                      width: '38px',
                      height: '38px',
                      borderRadius: '10px',
                      background: 'rgba(255,255,255,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,0.9)',
                      transition: 'all 0.3s ease'
                    }} onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                      e.currentTarget.style.color = 'white';
                    }} onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                      e.currentTarget.style.color = 'rgba(255,255,255,0.9)';
                    }}>
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                    </a>
                  )}
                  {company.socialLinks.instagram && (
                    <a href={company.socialLinks.instagram} target="_blank" rel="noopener noreferrer" style={{
                      width: '38px',
                      height: '38px',
                      borderRadius: '10px',
                      background: 'rgba(255,255,255,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,0.9)',
                      transition: 'all 0.3s ease'
                    }} onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                      e.currentTarget.style.color = 'white';
                    }} onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                      e.currentTarget.style.color = 'rgba(255,255,255,0.9)';
                    }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                    </a>
                  )}
                </div>
              )}
              
              {/* Login Button */}
              <button 
                onClick={() => setShowLoginModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: 'white',
                  color: primaryColor,
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.15)';
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                <span>Staff Login</span>
              </button>
              
              {/* Mobile Menu Toggle */}
              <button 
                className="st-mobile-toggle" 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{
                  flexDirection: 'column',
                  gap: '5px',
                  padding: '10px',
                  minWidth: '44px',
                  minHeight: '44px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                <span style={{width: '24px', height: '2px', background: 'white', borderRadius: '2px'}}></span>
                <span style={{width: '24px', height: '2px', background: 'white', borderRadius: '2px'}}></span>
                <span style={{width: '24px', height: '2px', background: 'white', borderRadius: '2px'}}></span>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="st-mobile-menu" style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '16px 20px',
              borderTop: '1px solid rgba(255,255,255,0.2)',
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 999,
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
            }}>
              {sections.about && <a href="#about" onClick={() => setMobileMenuOpen(false)} style={{padding: '14px 16px', color: 'white', textDecoration: 'none', fontWeight: '500', borderRadius: '10px', transition: 'all 0.2s ease'}}>About</a>}
              {sections.services && <a href="#services" onClick={() => setMobileMenuOpen(false)} style={{padding: '14px 16px', color: 'white', textDecoration: 'none', fontWeight: '500', borderRadius: '10px', transition: 'all 0.2s ease'}}>Services</a>}
              <a href="#features" onClick={() => setMobileMenuOpen(false)} style={{padding: '14px 16px', color: 'white', textDecoration: 'none', fontWeight: '500', borderRadius: '10px', transition: 'all 0.2s ease'}}>Why Us</a>
              <a href="#team" onClick={() => setMobileMenuOpen(false)} style={{padding: '14px 16px', color: 'white', textDecoration: 'none', fontWeight: '500', borderRadius: '10px', transition: 'all 0.2s ease'}}>Our Team</a>
              {sections.contact && <a href="#contact" onClick={() => setMobileMenuOpen(false)} style={{padding: '14px 16px', color: 'white', textDecoration: 'none', fontWeight: '500', borderRadius: '10px', transition: 'all 0.2s ease'}}>Contact</a>}
              <button 
                onClick={() => { setMobileMenuOpen(false); setShowLoginModal(true); }} 
                style={{
                  padding: '14px 16px', 
                  color: 'white', 
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  textAlign: 'left',
                  fontWeight: '600', 
                  borderRadius: '10px', 
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginTop: '8px'
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                Staff Login
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* ========== MAIN CONTENT ========== */}
      {!showBookingForm ? (
        <main className="st-main" style={{paddingTop: 'max(80px, calc(80px + env(safe-area-inset-top, 0px)))'}}>
          
          {/* ========== HERO SECTION - MODERN DESIGN ========== */}
          {sections.hero && (
            <section style={{
              position: 'relative',
              minHeight: 'min(90vh, 800px)',
              display: 'flex',
              alignItems: 'center',
              background: company.heroImage 
                ? `linear-gradient(135deg, ${primaryColor}ee 0%, ${secondaryColor}ee 100%), url(${company.heroImage})`
                : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              overflow: 'hidden'
            }}>
              {/* Animated Background Elements */}
              <div style={{position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none'}}>
                <div style={{
                  position: 'absolute',
                  top: '-10%',
                  right: '-5%',
                  width: '500px',
                  height: '500px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  filter: 'blur(60px)',
                  animation: 'float 8s ease-in-out infinite'
                }}></div>
                <div style={{
                  position: 'absolute',
                  bottom: '-10%',
                  left: '-5%',
                  width: '400px',
                  height: '400px',
                  background: 'rgba(255,255,255,0.08)',
                  borderRadius: '50%',
                  filter: 'blur(50px)',
                  animation: 'float 10s ease-in-out infinite reverse'
                }}></div>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '600px',
                  height: '600px',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '50%',
                  filter: 'blur(80px)'
                }}></div>
              </div>
              
              <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: '0 clamp(16px, 4vw, 24px)',
                position: 'relative',
                zIndex: 2,
                width: '100%'
              }}>
                <div style={{
                  maxWidth: '800px',
                  textAlign: 'center',
                  margin: '0 auto'
                }}>
                  {/* Trust Badge */}
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    padding: '10px 24px',
                    borderRadius: '50px',
                    marginBottom: '30px',
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}>
                    <span style={{
                      width: '24px',
                      height: '24px',
                      background: '#22c55e',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px'
                    }}>✓</span>
                    <span style={{color: 'white', fontWeight: '600', fontSize: '0.95rem'}}>
                      {template?.heroBadge || 'Licensed & Certified Professionals'}
                    </span>
                  </div>
                  
                  {/* Main Title - Maximum visibility */}
                  <h1 style={{
                    color: 'white',
                    fontSize: 'clamp(3rem, 8vw, 5rem)',
                    fontWeight: '900',
                    lineHeight: '1.05',
                    marginBottom: '30px',
                    textShadow: '0 0 20px rgba(0,0,0,0.8), 0 0 40px rgba(0,0,0,0.6), 0 4px 60px rgba(0,0,0,0.5), 2px 2px 0 rgba(0,0,0,0.3)',
                    letterSpacing: '-0.01em',
                    WebkitTextStroke: '1px rgba(255,255,255,0.1)',
                    background: 'linear-gradient(180deg, #ffffff 0%, #f0f0f0 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))'
                  }}>
                    {company.heroTitle || template?.heroTitle || company.name}
                  </h1>
                  
                  {/* Subtitle - Maximum visibility */}
                  <p style={{
                    color: 'white',
                    fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
                    lineHeight: '1.5',
                    marginBottom: '40px',
                    maxWidth: '700px',
                    margin: '0 auto clamp(24px, 5vw, 40px)',
                    fontWeight: '600',
                    textShadow: '0 0 15px rgba(0,0,0,0.8), 0 2px 20px rgba(0,0,0,0.6), 1px 1px 0 rgba(0,0,0,0.4)',
                    letterSpacing: '0.02em',
                    background: 'rgba(0,0,0,0.25)',
                    padding: 'clamp(14px, 3vw, 20px) clamp(18px, 4vw, 30px)',
                    borderRadius: '16px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.15)'
                  }}>
                    {company.heroSubtitle || template?.heroSubtitle || company.description || 'Your trusted partner for tax preparation, ITIN services, and more. Professional, bilingual service with maximum refund guarantee.'}
                  </p>
                  
                  {/* CTA Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    marginBottom: '50px'
                  }}>
                    <button 
                      onClick={() => document.querySelector('#team')?.scrollIntoView({ behavior: 'smooth' })}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: 'clamp(14px, 3vw, 18px) clamp(24px, 5vw, 36px)',
                        background: 'white',
                        color: primaryColor,
                        border: 'none',
                        borderRadius: '14px',
                        fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.2)';
                      }}
                    >
                      <span>📅</span>
                      <span>{company.heroCTAButtonText || template?.ctaText || 'Book Appointment'}</span>
                    </button>
                    {sections.contact && (
                      <a 
                        href="#contact" 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: 'clamp(14px, 3vw, 18px) clamp(24px, 5vw, 36px)',
                          background: 'rgba(255,255,255,0.15)',
                          color: 'white',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderRadius: '14px',
                          fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)',
                          fontWeight: '600',
                          textDecoration: 'none',
                          transition: 'all 0.3s ease',
                          backdropFilter: 'blur(10px)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                          e.currentTarget.style.transform = 'translateY(-4px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <span>📞</span>
                        <span>Contact Us</span>
                      </a>
                    )}
                  </div>
                  
                  {/* Trust Indicators */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '20px',
                    flexWrap: 'wrap'
                  }}>
                    {[
                      { icon: '✅', text: 'IRS Certified' },
                      { icon: '💯', text: 'Maximum Refund' },
                      { icon: '🌐', text: 'Bilingual Service' }
                    ].map((item, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(10px)',
                        padding: '10px 18px',
                        borderRadius: '30px',
                        border: '1px solid rgba(255,255,255,0.2)'
                      }}>
                        <span style={{fontSize: '1.2rem'}}>{item.icon}</span>
                        <span style={{fontWeight: '600', fontSize: '0.9rem', color: 'white', textShadow: '0 1px 3px rgba(0,0,0,0.5)'}}>{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Wave Divider */}
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '100px',
                overflow: 'hidden'
              }}>
                <svg viewBox="0 0 1440 100" preserveAspectRatio="none" style={{
                  position: 'absolute',
                  bottom: 0,
                  width: '100%',
                  height: '100%'
                }}>
                  <path d="M0,50 C360,100 1080,0 1440,50 L1440,100 L0,100 Z" fill="#f8fafc"/>
                </svg>
              </div>
            </section>
          )}

          {/* ========== OUR TEAM SECTION ========== */}
          <section id="team" style={{
            background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
            padding: 'clamp(60px, 10vw, 100px) clamp(15px, 4vw, 20px)',
            position: 'relative'
          }}>
            <div style={{maxWidth: '1200px', margin: '0 auto'}}>
              <div style={{textAlign: 'center', marginBottom: 'clamp(35px, 8vw, 60px)', padding: '0 10px'}}>
                <span style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                  color: 'white',
                    padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                    borderRadius: '30px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    fontWeight: '600',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'inline-block',
                    boxShadow: `0 4px 15px ${primaryColor}40`
                  }}>👥 Our Team</span>
                  <h2 style={{
                    color: '#1a1a2e',
                    fontSize: 'clamp(1.5rem, 5vw, 3rem)',
                    marginTop: 'clamp(18px, 4vw, 25px)',
                    fontWeight: '800',
                    lineHeight: '1.2'
                  }}>{company.preparersSectionTitle || 'Our Tax Preparers'}</h2>
                  <p style={{color: '#64748b', fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)', marginTop: '15px', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto'}}>
                    Select a professional to book your appointment
                  </p>
                </div>
                
                {preparers.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: 'clamp(30px, 6vw, 60px) clamp(20px, 5vw, 40px)',
                    background: 'white',
                    borderRadius: '24px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.08)'
                  }}>
                    <div style={{fontSize: 'clamp(3rem, 8vw, 5rem)', marginBottom: '20px'}}>👥</div>
                    <h3 style={{color: '#1a1a2e', fontSize: '1.5rem', marginBottom: '10px'}}>No Team Members Yet</h3>
                    <p style={{color: '#64748b', fontSize: '1.1rem'}}>Team members will appear here once added</p>
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
                    gap: 'clamp(20px, 4vw, 35px)',
                    justifyItems: 'center',
                    padding: '0 clamp(10px, 3vw, 20px)'
                  }}>
                    {preparers.map((p) => (
                      <div 
                        key={p.id} 
                        onClick={() => handleSelectPreparer(p)} 
                        style={{
                          background: 'white',
                          borderRadius: '24px',
                          overflow: 'hidden',
                          boxShadow: '0 15px 50px rgba(0,0,0,0.1)',
                          cursor: 'pointer',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          width: '100%',
                          maxWidth: '380px',
                          border: '1px solid rgba(0,0,0,0.05)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                          e.currentTarget.style.boxShadow = `0 25px 60px ${primaryColor}30`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          e.currentTarget.style.boxShadow = '0 15px 50px rgba(0,0,0,0.1)';
                        }}
                      >
                        {/* Photo Section */}
                        <div style={{
                          padding: 'clamp(25px, 5vw, 40px) clamp(20px, 4vw, 30px) clamp(15px, 3vw, 20px)',
                          background: `linear-gradient(135deg, ${primaryColor}08 0%, ${secondaryColor}08 100%)`,
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          <div style={{
                            width: 'clamp(120px, 30vw, 160px)',
                            height: 'clamp(120px, 30vw, 160px)',
                            borderRadius: '50%',
                            padding: '4px',
                            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                            boxShadow: `0 10px 30px ${primaryColor}40`
                          }}>
                            <div style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '50%',
                              overflow: 'hidden',
                              background: '#f0f0f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {p.photo ? (
                                <img src={p.photo} alt={p.name} style={{width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top'}} />
                              ) : (
                                <div style={{width: '100%', height: '100%', background: `linear-gradient(135deg, ${primaryColor}30 0%, ${secondaryColor}30 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4rem', color: primaryColor}}>👤</div>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Info Section */}
                        <div style={{padding: 'clamp(18px, 4vw, 25px) clamp(15px, 3vw, 30px) clamp(25px, 5vw, 35px)', textAlign: 'center'}}>
                          <h3 style={{color: '#1a1a2e', fontSize: 'clamp(1.2rem, 3.5vw, 1.5rem)', marginBottom: '8px', fontWeight: '700'}}>{p.name || 'Tax Professional'}</h3>
                          <p style={{color: primaryColor, fontSize: 'clamp(0.85rem, 2.5vw, 0.95rem)', fontWeight: '600', marginBottom: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'}}>
                            <span style={{width: '8px', height: '8px', background: '#22c55e', borderRadius: '50%', display: 'inline-block', flexShrink: 0}}></span>
                            {p.role || 'Tax Professional'}
                          </p>
                          <div style={{display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '15px'}}>
                            {[1,2,3,4,5].map(star => (<span key={star} style={{color: '#fbbf24', fontSize: '1.1rem'}}>★</span>))}
                            <span style={{color: '#64748b', fontSize: '0.85rem', marginLeft: '5px'}}>(5.0)</span>
                          </div>
                          
                          {/* Bio Section */}
                          {p.bio && (
                            <p style={{
                              color: '#64748b',
                              fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
                              lineHeight: '1.6',
                              marginBottom: '15px',
                              padding: 'clamp(10px, 2vw, 12px) clamp(12px, 3vw, 15px)',
                              background: '#f8fafc',
                              borderRadius: '12px',
                              borderLeft: `3px solid ${primaryColor}`,
                              textAlign: 'left',
                              fontStyle: 'italic',
                              wordBreak: 'break-word'
                            }}>
                              {p.bio}
                            </p>
                          )}
                          
                          {/* Contact Info */}
                          {(p.email || p.phone) && (
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                              marginBottom: '20px',
                              padding: 'clamp(10px, 2vw, 12px)',
                              background: `linear-gradient(135deg, ${primaryColor}05 0%, ${secondaryColor}05 100%)`,
                              borderRadius: '12px'
                            }}>
                              {p.email && (
                                <a href={`mailto:${p.email}`} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '8px',
                                  color: '#475569',
                                  fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)',
                                  textDecoration: 'none',
                                  transition: 'color 0.2s ease',
                                  wordBreak: 'break-all'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#475569'}
                                onClick={(e) => e.stopPropagation()}
                                >
                                  <span style={{fontSize: '1rem', flexShrink: 0}}>✉️</span>
                                  <span style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{p.email}</span>
                                </a>
                              )}
                              {p.phone && (
                                <a href={`tel:${p.phone}`} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '8px',
                                  color: '#475569',
                                  fontSize: 'clamp(0.75rem, 2.5vw, 0.85rem)',
                                  textDecoration: 'none',
                                  transition: 'color 0.2s ease'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#475569'}
                                onClick={(e) => e.stopPropagation()}
                                >
                                  <span style={{fontSize: '1rem', flexShrink: 0}}>📞</span>
                                  <span>{p.phone}</span>
                                </a>
                              )}
                            </div>
                          )}
                          
                          <button style={{
                            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, 
                            color: 'white', 
                            border: 'none', 
                            padding: 'clamp(12px, 2.5vw, 14px) clamp(25px, 6vw, 40px)', 
                            borderRadius: '50px', 
                            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)', 
                            fontWeight: '600', 
                            cursor: 'pointer', 
                            transition: 'all 0.3s ease', 
                            boxShadow: `0 8px 25px ${primaryColor}40`, 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            width: '100%',
                            maxWidth: '250px',
                            justifyContent: 'center'
                          }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = `0 12px 35px ${primaryColor}50`; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = `0 8px 25px ${primaryColor}40`; }}
                          >📅 Book Appointment</button>
                          
                          {/* Share Link Button */}
                          <button 
                            onClick={(e) => handleCopyPreparerLink(e, p)}
                            style={{
                              background: 'white',
                              color: primaryColor,
                              border: `2px solid ${primaryColor}`,
                              padding: 'clamp(10px, 2vw, 12px) clamp(20px, 4vw, 30px)',
                              borderRadius: '50px',
                              fontSize: 'clamp(0.8rem, 2.2vw, 0.9rem)',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              marginTop: '10px',
                              width: '100%',
                              maxWidth: '250px',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => { 
                              e.currentTarget.style.background = primaryColor;
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => { 
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.color = primaryColor;
                            }}
                            title={`Copy booking link for ${p.name}`}
                          >🔗 Share Booking Link</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

          {/* ========== WHY CHOOSE US SECTION ========== */}
          <section id="features" style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            padding: 'clamp(60px, 10vw, 100px) clamp(15px, 4vw, 20px)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{position: 'absolute', top: '50px', left: '10%', width: 'min(200px, 40vw)', height: 'min(200px, 40vw)', background: `${primaryColor}10`, borderRadius: '50%', filter: 'blur(60px)', zIndex: 0}}></div>
            <div style={{position: 'absolute', bottom: '50px', right: '10%', width: 'min(250px, 45vw)', height: 'min(250px, 45vw)', background: `${secondaryColor}10`, borderRadius: '50%', filter: 'blur(80px)', zIndex: 0}}></div>
            <div style={{maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1}}>
              <div style={{textAlign: 'center', marginBottom: 'clamp(30px, 6vw, 60px)'}}>
                <span style={{background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, color: 'white', padding: '10px 24px', borderRadius: '30px', fontSize: '14px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'inline-block', boxShadow: `0 4px 15px ${primaryColor}40`}}>✨ Why Choose Us</span>
                <h2 style={{color: '#1a1a2e', fontSize: 'clamp(2rem, 5vw, 3rem)', marginTop: '25px', fontWeight: '800', lineHeight: '1.2'}}>{company.featuresTitle || template?.featuresTitle || 'Why Choose Us?'}</h2>
                <p style={{color: '#64748b', fontSize: '1.1rem', marginTop: '15px', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto'}}>Experience the difference with our professional services</p>
              </div>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'clamp(15px, 4vw, 30px)'}}>
                {(company.featuresList && company.featuresList.length > 0 
                  ? company.featuresList 
                  : company.landingPageSettings?.features && company.landingPageSettings.features.length > 0
                    ? company.landingPageSettings.features
                    : company.feature1Title 
                      ? [
                          { icon: company.feature1Icon, title: company.feature1Title, description: company.feature1Text },
                          { icon: company.feature2Icon, title: company.feature2Title, description: company.feature2Text },
                          { icon: company.feature3Icon, title: company.feature3Title, description: company.feature3Text },
                          { icon: company.feature4Icon, title: company.feature4Title, description: company.feature4Text }
                        ].filter(f => f.title)
                      : template?.features || [
                          {icon: '✅', title: 'IRS Certified', description: 'All preparers are IRS certified and trained professionals'},
                          {icon: '💵', title: 'Maximum Refund', description: 'We guarantee to get you the maximum refund possible'},
                          {icon: '🌐', title: 'Bilingual Service', description: 'Fluent in English and Spanish to serve you better'},
                          {icon: '📞', title: 'Year-Round Support', description: 'We are here for you all year, not just tax season'}
                        ]
                ).map((feature, i) => (
                  <div key={i} style={{background: 'white', padding: 'clamp(24px, 5vw, 40px) clamp(18px, 4vw, 30px)', borderRadius: '24px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.05)', transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden'}}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-10px)'; e.currentTarget.style.boxShadow = `0 20px 50px ${primaryColor}25`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,0.08)'; }}
                  >
                    <div style={{width: '80px', height: '80px', background: `linear-gradient(135deg, ${primaryColor}15 0%, ${secondaryColor}15 100%)`, borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '2.5rem'}}>{sanitizeIcon(feature.icon, i)}</div>
                    <h3 style={{color: '#1a1a2e', fontSize: '1.3rem', marginBottom: '12px', fontWeight: '700'}}>{feature.title}</h3>
                    <p style={{color: '#64748b', fontSize: '0.95rem', lineHeight: '1.7'}}>{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ========== ABOUT SECTION ========== */}
          {sections.about && (
            <section id="about" style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
              padding: 'clamp(60px, 10vw, 100px) clamp(15px, 4vw, 20px)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{maxWidth: '1200px', margin: '0 auto'}}>
                <div style={{textAlign: 'center', marginBottom: 'clamp(35px, 8vw, 60px)' }}>
                  <span style={{
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    color: 'white',
                    padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                    borderRadius: '30px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    fontWeight: '600',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'inline-block',
                    boxShadow: `0 4px 15px ${primaryColor}40`
                  }}>📖 About Us</span>
                  <h2 style={{
                    color: '#1a1a2e',
                    fontSize: 'clamp(1.5rem, 5vw, 3rem)',
                    marginTop: 'clamp(18px, 4vw, 25px)',
                    fontWeight: '800',
                    lineHeight: '1.2'
                  }}>{company.aboutTitle || template?.aboutTitle || 'Who We Are'}</h2>
                </div>
                
                <div style={{
                  display: 'flex',
                  flexDirection: company.aboutImage ? 'row' : 'column',
                  gap: 'clamp(30px, 6vw, 60px)',
                  alignItems: company.aboutImage ? 'center' : 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    flex: company.aboutImage ? '1 1 400px' : '1 1 100%',
                    maxWidth: company.aboutImage ? '600px' : '800px',
                    textAlign: company.aboutImage ? 'left' : 'center'
                  }}>
                    <p style={{
                      color: '#64748b',
                      fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)',
                      lineHeight: '1.85',
                      marginBottom: 'clamp(25px, 5vw, 36px)'
                    }}>{company.aboutText || template?.aboutDescription || company.description || 'Friend Tax Services is dedicated to providing professional, reliable tax preparation and financial services to our community. With years of experience and a commitment to excellence, we help individuals and businesses navigate the complexities of tax filing while maximizing their returns.'}</p>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))',
                      gap: 'clamp(10px, 2vw, 18px)'
                    }}>
                      {(template?.aboutHighlights || [
                        { text: 'IRS Certified Tax Preparers', icon: '✓' },
                        { text: 'Bilingual Service (English/Spanish)', icon: '✓' },
                        { text: 'Maximum Refund Guarantee', icon: '✓' },
                        { text: 'Year-Round Support', icon: '✓' }
                      ]).map((highlight, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'clamp(10px, 2vw, 16px)',
                          fontWeight: '600',
                          fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)',
                          color: '#1a1a2e',
                          padding: 'clamp(10px, 2vw, 14px) clamp(14px, 3vw, 20px)',
                          background: 'white',
                          borderRadius: '14px',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                          border: '1px solid rgba(0, 0, 0, 0.04)',
                          transition: 'all 0.3s ease'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px',
                            minWidth: '36px',
                            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                            color: 'white',
                            fontSize: '0.9rem',
                            borderRadius: '10px',
                            boxShadow: `0 4px 12px ${primaryColor}40`
                          }}>{sanitizeIcon(highlight.icon, idx) || '✓'}</div>
                          <span>{highlight.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {company.aboutImage && (
                    <div style={{
                      flex: '1 1 350px',
                      maxWidth: '500px',
                      position: 'relative'
                    }}>
                      <img src={company.aboutImage} alt="About us" style={{
                        width: '100%',
                        borderRadius: '24px',
                        boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)',
                        transition: 'transform 0.4s ease'
                      }} />
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* ========== SERVICES SECTION WITH SLIDER ========== */}
          {sections.services && (() => {
            const servicesList = company.servicesList && company.servicesList.length > 0 
              ? company.servicesList 
              : company.landingPageSettings?.services && company.landingPageSettings.services.length > 0
                ? company.landingPageSettings.services
                : template?.services && template.services.length > 0 
                  ? template.services 
                  : [
                    { icon: '📋', title: 'Personal Tax Filing', description: 'Expert individual tax preparation with maximum refund guarantee' },
                    { icon: '🏢', title: 'Business Tax Services', description: 'Complete business tax solutions for all entity types' },
                    { icon: '💼', title: 'ITIN Applications', description: 'Fast and reliable ITIN processing and renewals' },
                    { icon: '📊', title: 'Tax Planning & Consulting', description: 'Year-round tax planning strategies to minimize liability' }
                  ];
            
            const useSlider = servicesList.length > 1;
            
            return (
              <section id="services" style={{
                position: 'relative',
                padding: 'clamp(60px, 10vw, 100px) clamp(15px, 4vw, 20px)',
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`
              }}>
                <div style={{maxWidth: '1200px', margin: '0 auto'}}>
                  <div style={{textAlign: 'center', marginBottom: 'clamp(30px, 6vw, 60px)'}}>
                    <h2 style={{color: 'white', fontSize: 'clamp(1.5rem, 5vw, 3rem)', fontWeight: '800', lineHeight: '1.2'}}>{company.servicesTitle || template?.servicesTitle || 'Our Services'}</h2>
                    <p style={{color: 'rgba(255,255,255,0.9)', fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', marginTop: '15px', padding: '0 10px', maxWidth: '650px', marginLeft: 'auto', marginRight: 'auto'}}>{company.servicesSubtitle || template?.servicesSubtitle || 'Professional tax preparation and financial services tailored to your needs'}</p>
                  </div>
                  
                  {useSlider ? (
                    <div 
                      style={{position: 'relative', padding: '0 clamp(25px, 5vw, 40px)'}}
                      onMouseEnter={() => { sliderHoveredRef.current = true; }}
                      onMouseLeave={() => { sliderHoveredRef.current = false; }}
                    >
                      <button 
                        onClick={() => {
                          setCurrentServiceIndex((prev) => {
                            const newIndex = prev <= 0 ? servicesList.length - 1 : prev - 1;
                            const container = document.getElementById('services-slider');
                            const firstCard = container?.querySelector(':scope > div');
                            const gap = container ? parseFloat(getComputedStyle(container).gap) || 20 : 20;
                            const cardWidth = firstCard ? firstCard.offsetWidth + gap : 300;
                            container?.scrollTo({left: newIndex * cardWidth, behavior: 'smooth'});
                            return newIndex;
                          });
                        }}
                        style={{
                          position: 'absolute', left: '0', top: '50%', transform: 'translateY(-50%)', zIndex: 10,
                          background: 'white', border: 'none', borderRadius: '50%',
                          width: 'clamp(36px, 8vw, 50px)', height: 'clamp(36px, 8vw, 50px)',
                          cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 'clamp(1rem, 3vw, 1.5rem)', color: primaryColor
                        }}
                        aria-label="Previous services"
                      >←</button>
                      
                      <div 
                        id="services-slider"
                        style={{
                          display: 'flex', gap: 'clamp(15px, 4vw, 30px)', overflowX: 'auto',
                          scrollBehavior: 'smooth', padding: '20px 5px',
                          scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch'
                        }}
                      >
                        <style>{`#services-slider::-webkit-scrollbar { display: none; }`}</style>
                        {servicesList.map((service, index) => (
                          <div key={index} style={{ 
                            minWidth: 'clamp(240px, 70vw, 300px)', maxWidth: 'clamp(240px, 70vw, 300px)',
                            background: 'white', padding: 'clamp(25px, 5vw, 40px) clamp(20px, 4vw, 32px)',
                            borderRadius: 'clamp(12px, 3vw, 20px)', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', flexShrink: 0
                          }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              width: 'clamp(60px, 15vw, 80px)', height: 'clamp(60px, 15vw, 80px)',
                              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                              borderRadius: 'clamp(12px, 3vw, 18px)', marginBottom: 'clamp(16px, 4vw, 24px)',
                              fontSize: 'clamp(1.8rem, 6vw, 2.5rem)'
                            }}>
                              <span>{sanitizeIcon(service.icon, index) || '📋'}</span>
                            </div>
                            <h3 style={{color: '#1a1a2e', fontSize: 'clamp(1.1rem, 3.5vw, 1.4rem)', fontWeight: '700', marginBottom: '12px'}}>{service.title || service.name}</h3>
                            <p style={{color: '#64748b', fontSize: 'clamp(0.85rem, 2.5vw, 1rem)', lineHeight: '1.6'}}>{service.description}</p>
                          </div>
                        ))}
                      </div>
                      
                      <button 
                        onClick={() => {
                          setCurrentServiceIndex((prev) => {
                            const newIndex = (prev + 1) % servicesList.length;
                            const container = document.getElementById('services-slider');
                            const firstCard = container?.querySelector(':scope > div');
                            const gap = container ? parseFloat(getComputedStyle(container).gap) || 20 : 20;
                            const cardWidth = firstCard ? firstCard.offsetWidth + gap : 300;
                            container?.scrollTo({left: newIndex * cardWidth, behavior: 'smooth'});
                            return newIndex;
                          });
                        }}
                        style={{
                          position: 'absolute', right: '0', top: '50%', transform: 'translateY(-50%)', zIndex: 10,
                          background: 'white', border: 'none', borderRadius: '50%',
                          width: 'clamp(36px, 8vw, 50px)', height: 'clamp(36px, 8vw, 50px)',
                          cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 'clamp(1rem, 3vw, 1.5rem)', color: primaryColor
                        }}
                        aria-label="Next services"
                      >→</button>
                      
                      <div style={{display: 'flex', justifyContent: 'center', gap: 'clamp(6px, 1.5vw, 8px)', marginTop: 'clamp(20px, 4vw, 30px)', flexWrap: 'wrap'}}>
                        {servicesList.map((_, index) => (
                          <button key={index}
                            onClick={() => {
                              setCurrentServiceIndex(index);
                              const container = document.getElementById('services-slider');
                              const firstCard = container?.querySelector(':scope > div');
                              const gap = container ? parseFloat(getComputedStyle(container).gap) || 20 : 20;
                              const cardWidth = firstCard ? firstCard.offsetWidth + gap : 300;
                              container?.scrollTo({left: index * cardWidth, behavior: 'smooth'});
                            }}
                            style={{
                              width: currentServiceIndex === index ? 'clamp(24px, 5vw, 30px)' : 'clamp(10px, 2.5vw, 12px)',
                              height: 'clamp(10px, 2.5vw, 12px)',
                              borderRadius: currentServiceIndex === index ? '6px' : '50%',
                              background: currentServiceIndex === index ? 'white' : 'rgba(255,255,255,0.5)',
                              border: 'none', cursor: 'pointer', transition: 'all 0.3s ease'
                            }}
                            aria-label={`Go to service ${index + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 'clamp(15px, 4vw, 30px)'}}>
                      {servicesList.map((service, index) => (
                        <div key={index} style={{ 
                          background: 'white', padding: 'clamp(25px, 5vw, 40px) clamp(20px, 4vw, 32px)',
                          borderRadius: 'clamp(12px, 3vw, 20px)', boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
                        }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 'clamp(60px, 15vw, 80px)', height: 'clamp(60px, 15vw, 80px)',
                            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                            borderRadius: 'clamp(12px, 3vw, 18px)', marginBottom: 'clamp(16px, 4vw, 24px)',
                            fontSize: 'clamp(1.8rem, 6vw, 2.5rem)'
                          }}>
                            <span>{sanitizeIcon(service.icon, index) || '📋'}</span>
                          </div>
                          <h3 style={{color: '#1a1a2e', fontSize: 'clamp(1.1rem, 3.5vw, 1.4rem)', fontWeight: '700', marginBottom: '12px'}}>{service.title || service.name}</h3>
                          <p style={{color: '#64748b', fontSize: 'clamp(0.85rem, 2.5vw, 1rem)', lineHeight: '1.6'}}>{service.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            );
          })()}

          {/* ========== TESTIMONIALS SECTION ========== */}
          {sections.testimonials && (() => {
            const testimonialsList = company.testimonials && company.testimonials.length > 0 
              ? company.testimonials 
              : template?.testimonials || [];
            if (testimonialsList.length === 0) return null;
            return (
              <section style={{
                background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                padding: 'clamp(60px, 10vw, 100px) clamp(15px, 4vw, 20px)',
                position: 'relative'
              }}>
                <div style={{maxWidth: '1200px', margin: '0 auto'}}>
                  <div style={{textAlign: 'center', marginBottom: 'clamp(35px, 8vw, 60px)' }}>
                    <span style={{
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                      color: 'white',
                      padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                      borderRadius: '30px',
                      fontSize: 'clamp(12px, 2.5vw, 14px)',
                      fontWeight: '600',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      display: 'inline-block',
                      boxShadow: `0 4px 15px ${primaryColor}40`
                    }}>⭐ Testimonials</span>
                    <h2 style={{
                      color: '#1a1a2e',
                      fontSize: 'clamp(1.5rem, 5vw, 3rem)',
                      marginTop: 'clamp(18px, 4vw, 25px)',
                      fontWeight: '800',
                      lineHeight: '1.2'
                    }}>{company.testimonialsTitle || template?.testimonialsTitle || 'What Our Clients Say'}</h2>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
                    gap: 'clamp(15px, 4vw, 28px)'
                  }}>
                    {testimonialsList.map((testimonial, index) => (
                      <div key={index} style={{
                        background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                        padding: 'clamp(24px, 5vw, 32px) clamp(20px, 4vw, 28px)',
                        borderRadius: '20px',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
                        position: 'relative'
                      }}>
                        <div style={{
                          fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
                          fontWeight: '800',
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          lineHeight: '1',
                          marginBottom: '-6px',
                          opacity: 0.8
                        }}>"</div>
                        <div style={{color: '#ffc107', fontSize: '1.1rem', marginBottom: '16px', letterSpacing: '2px'}}>
                          {[...Array(testimonial.rating || 5)].map((_, i) => (
                            <span key={i}>★</span>
                          ))}
                        </div>
                        <p style={{
                          fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)',
                          color: '#475569',
                          lineHeight: '1.75',
                          fontStyle: 'italic',
                          marginBottom: '24px'
                        }}>{testimonial.text}</p>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '14px',
                          paddingTop: '16px',
                          borderTop: '1px solid rgba(0, 0, 0, 0.06)'
                        }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            minWidth: '48px',
                            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                            color: 'white',
                            borderRadius: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '700',
                            fontSize: '1.2rem',
                            boxShadow: `0 4px 12px ${primaryColor}40`
                          }}>
                            {testimonial.name?.charAt(0) || 'C'}
                          </div>
                          <div style={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
                            <span style={{fontWeight: '700', color: '#1a1a2e', fontSize: '1rem'}}>{testimonial.name}</span>
                            {testimonial.role && <span style={{fontSize: '0.85rem', color: '#64748b'}}>{testimonial.role}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          })()}

          {/* ========== CTA SECTION ========== */}
          {sections.cta && (
            <section style={{
              position: 'relative',
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
              overflow: 'hidden',
              padding: 'clamp(60px, 10vw, 100px) clamp(15px, 4vw, 20px)'
            }}>
              <div style={{maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center', color: 'white'}}>
                <h2 style={{
                  fontSize: 'clamp(1.5rem, 5vw, 2.8rem)',
                  fontWeight: '800',
                  marginBottom: '20px',
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  lineHeight: '1.2'
                }}>{company.ctaTitle || template?.footerCtaTitle || 'Ready to Get Your Maximum Refund?'}</h2>
                <p style={{
                  fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                  opacity: 0.95,
                  marginBottom: '36px',
                  maxWidth: '650px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  lineHeight: '1.7'
                }}>{company.ctaText || template?.footerCtaSubtitle || 'Schedule your appointment today with our IRS certified tax professionals. Bilingual service available!'}</p>
                <button 
                  onClick={() => document.querySelector('#team')?.scrollIntoView({ behavior: 'smooth' })}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: 'clamp(14px, 3vw, 18px) clamp(28px, 6vw, 40px)',
                    background: 'white',
                    color: primaryColor,
                    border: 'none',
                    borderRadius: '14px',
                    fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.2)';
                  }}
                >
                  <span>{company.ctaButtonText || template?.footerCtaButton || 'Book Now'}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                  </svg>
                </button>
              </div>
            </section>
          )}

          {/* ========== CONTACT SECTION ========== */}
          {sections.contact && (
            <section id="contact" style={{
              background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
              padding: 'clamp(60px, 10vw, 100px) clamp(15px, 4vw, 20px)',
              position: 'relative'
            }}>
              <div style={{maxWidth: '1200px', margin: '0 auto'}}>
                <div style={{textAlign: 'center', marginBottom: 'clamp(35px, 8vw, 60px)' }}>
                  <span style={{
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                    color: 'white',
                    padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                    borderRadius: '30px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    fontWeight: '600',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'inline-block',
                    boxShadow: `0 4px 15px ${primaryColor}40`
                  }}>📞 Contact</span>
                  <h2 style={{
                    color: '#1a1a2e',
                    fontSize: 'clamp(1.5rem, 5vw, 3rem)',
                    marginTop: 'clamp(18px, 4vw, 25px)',
                    fontWeight: '800',
                    lineHeight: '1.2'
                  }}>{company.contactTitle || template?.contactTitle || 'Get In Touch'}</h2>
                  <p style={{color: '#64748b', fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)', marginTop: '15px', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto'}}>
                    {company.contactSubtitle || template?.contactSubtitle || 'Have questions? We\'re here to help.'}
                  </p>
                </div>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: (company.showMap && company.mapEmbedUrl) ? 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))' : '1fr',
                  gap: 'clamp(25px, 5vw, 48px)',
                  alignItems: 'start',
                  maxWidth: (company.showMap && company.mapEmbedUrl) ? '1200px' : '700px',
                  margin: '0 auto'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
                    gap: 'clamp(12px, 3vw, 16px)'
                  }}>
                    {(company.phone || template?.contactPhone) && (
                      <a href={`tel:${company.phone || template?.contactPhone}`} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'clamp(14px, 3vw, 20px)',
                        padding: 'clamp(18px, 4vw, 24px) clamp(20px, 4vw, 28px)',
                        background: 'white',
                        borderRadius: '16px',
                        textDecoration: 'none',
                        color: 'inherit',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(0, 0, 0, 0.06)',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.04)'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 'clamp(44px, 10vw, 56px)',
                          height: 'clamp(44px, 10vw, 56px)',
                          minWidth: 'clamp(44px, 10vw, 56px)',
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                          borderRadius: '14px',
                          boxShadow: `0 6px 20px ${primaryColor}30`
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="24" height="24">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                          </svg>
                        </div>
                        <div>
                          <h4 style={{fontSize: '0.8rem', fontWeight: '700', color: primaryColor, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px'}}>Phone</h4>
                          <p style={{fontSize: 'clamp(0.95rem, 2.5vw, 1.05rem)', fontWeight: '600', color: '#1a1a2e', margin: 0, lineHeight: '1.4'}}>{company.phone || template?.contactPhone}</p>
                        </div>
                      </a>
                    )}
                    {(company.email || template?.contactEmail) && (
                      <a href={`mailto:${company.email || template?.contactEmail}`} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'clamp(14px, 3vw, 20px)',
                        padding: 'clamp(18px, 4vw, 24px) clamp(20px, 4vw, 28px)',
                        background: 'white',
                        borderRadius: '16px',
                        textDecoration: 'none',
                        color: 'inherit',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(0, 0, 0, 0.06)',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.04)'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 'clamp(44px, 10vw, 56px)',
                          height: 'clamp(44px, 10vw, 56px)',
                          minWidth: 'clamp(44px, 10vw, 56px)',
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                          borderRadius: '14px',
                          boxShadow: `0 6px 20px ${primaryColor}30`
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="24" height="24">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                          </svg>
                        </div>
                        <div>
                          <h4 style={{fontSize: '0.8rem', fontWeight: '700', color: primaryColor, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px'}}>Email</h4>
                          <p style={{fontSize: 'clamp(0.95rem, 2.5vw, 1.05rem)', fontWeight: '600', color: '#1a1a2e', margin: 0, lineHeight: '1.4', wordBreak: 'break-word'}}>{company.email || template?.contactEmail}</p>
                        </div>
                      </a>
                    )}
                    {(company.address || template?.contactAddress) && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'clamp(14px, 3vw, 20px)',
                        padding: 'clamp(18px, 4vw, 24px) clamp(20px, 4vw, 28px)',
                        background: 'white',
                        borderRadius: '16px',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(0, 0, 0, 0.06)',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.04)'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 'clamp(44px, 10vw, 56px)',
                          height: 'clamp(44px, 10vw, 56px)',
                          minWidth: 'clamp(44px, 10vw, 56px)',
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                          borderRadius: '14px',
                          boxShadow: `0 6px 20px ${primaryColor}30`
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="24" height="24">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                          </svg>
                        </div>
                        <div>
                          <h4 style={{fontSize: '0.8rem', fontWeight: '700', color: primaryColor, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px'}}>Address</h4>
                          <p style={{fontSize: 'clamp(0.95rem, 2.5vw, 1.05rem)', fontWeight: '600', color: '#1a1a2e', margin: 0, lineHeight: '1.4', wordBreak: 'break-word'}}>{company.address || template?.contactAddress}</p>
                        </div>
                      </div>
                    )}
                    {(company.businessHours || template?.businessHours) && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'clamp(14px, 3vw, 20px)',
                        padding: 'clamp(18px, 4vw, 24px) clamp(20px, 4vw, 28px)',
                        background: 'white',
                        borderRadius: '16px',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(0, 0, 0, 0.06)',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.04)'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 'clamp(44px, 10vw, 56px)',
                          height: 'clamp(44px, 10vw, 56px)',
                          minWidth: 'clamp(44px, 10vw, 56px)',
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                          borderRadius: '14px',
                          boxShadow: `0 6px 20px ${primaryColor}30`
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="24" height="24">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                          </svg>
                        </div>
                        <div>
                          <h4 style={{fontSize: '0.8rem', fontWeight: '700', color: primaryColor, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px'}}>Business Hours</h4>
                          <p style={{fontSize: 'clamp(0.95rem, 2.5vw, 1.05rem)', fontWeight: '600', color: '#1a1a2e', margin: 0, lineHeight: '1.4'}}>{company.businessHours || template?.businessHours}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {company.showMap && company.mapEmbedUrl && (
                    <div style={{
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      border: '4px solid white'
                    }}>
                      <iframe 
                        src={company.mapEmbedUrl}
                        width="100%"
                        height="350"
                        style={{ border: 0, borderRadius: '12px' }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Location"
                      />
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* ========== FOOTER - MODERN DESIGN ========== */}
          <footer style={{
            background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1e 100%)',
            color: 'white',
            paddingTop: 'clamp(40px, 8vw, 80px)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Background decoration */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: `linear-gradient(90deg, transparent, ${primaryColor}50, transparent)`
            }}></div>
            
            <div style={{maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(16px, 4vw, 24px)'}}>
              {/* Footer Main Content */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))',
                gap: 'clamp(30px, 6vw, 50px)',
                paddingBottom: 'clamp(30px, 6vw, 60px)',
                borderBottom: '1px solid rgba(255,255,255,0.1)'
              }}>
                {/* Brand Column */}
                <div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '20px'
                  }}>
                    {(company.landingPageSettings?.logoUrl || company.logoUrl || company.logo) ? (
                      <img src={company.landingPageSettings?.logoUrl || company.logoUrl || company.logo} alt={company.name} style={{
                        height: '50px',
                        width: 'auto',
                        maxWidth: '180px',
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }} />
                    ) : (
                      <div style={{
                        width: '50px',
                        height: '50px',
                        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem'
                      }}>📊</div>
                    )}
                    <span style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: 'white',
                      textShadow: '0 2px 10px rgba(0,0,0,0.3)'
                    }}>{company.name}</span>
                  </div>
                  <p style={{
                    color: 'rgba(255,255,255,0.85)',
                    lineHeight: '1.8',
                    marginBottom: '25px',
                    fontSize: '0.95rem'
                  }}>
                    {company.description || 'Professional tax and financial services tailored to your needs.'}
                  </p>
                  
                  {/* Social Links */}
                  {company.socialLinks && (
                    <div style={{display: 'flex', gap: '12px'}}>
                      {company.socialLinks.facebook && (
                        <a href={company.socialLinks.facebook} target="_blank" rel="noopener noreferrer" style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '12px',
                          background: 'rgba(255,255,255,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'rgba(255,255,255,0.8)',
                          transition: 'all 0.3s ease'
                        }} onMouseEnter={(e) => {
                          e.currentTarget.style.background = primaryColor;
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.transform = 'translateY(-3px)';
                        }} onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                          e.currentTarget.style.color = 'rgba(255,255,255,0.8)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}>
                          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                        </a>
                      )}
                      {company.socialLinks.twitter && (
                        <a href={company.socialLinks.twitter} target="_blank" rel="noopener noreferrer" style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '12px',
                          background: 'rgba(255,255,255,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'rgba(255,255,255,0.8)',
                          transition: 'all 0.3s ease'
                        }} onMouseEnter={(e) => {
                          e.currentTarget.style.background = primaryColor;
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.transform = 'translateY(-3px)';
                        }} onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                          e.currentTarget.style.color = 'rgba(255,255,255,0.8)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}>
                          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/></svg>
                        </a>
                      )}
                      {company.socialLinks.instagram && (
                        <a href={company.socialLinks.instagram} target="_blank" rel="noopener noreferrer" style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '12px',
                          background: 'rgba(255,255,255,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'rgba(255,255,255,0.8)',
                          transition: 'all 0.3s ease'
                        }} onMouseEnter={(e) => {
                          e.currentTarget.style.background = primaryColor;
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.transform = 'translateY(-3px)';
                        }} onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                          e.currentTarget.style.color = 'rgba(255,255,255,0.8)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                        </a>
                      )}
                      {company.socialLinks.linkedin && (
                        <a href={company.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '12px',
                          background: 'rgba(255,255,255,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'rgba(255,255,255,0.8)',
                          transition: 'all 0.3s ease'
                        }} onMouseEnter={(e) => {
                          e.currentTarget.style.background = primaryColor;
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.transform = 'translateY(-3px)';
                        }} onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                          e.currentTarget.style.color = 'rgba(255,255,255,0.8)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}>
                          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
                        </a>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Quick Links */}
                <div>
                  <h4 style={{
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    marginBottom: '25px',
                    color: 'white'
                  }}>Quick Links</h4>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '14px'}}>
                    {sections.about && (
                      <a href="#about" style={{
                        color: 'rgba(255,255,255,0.7)',
                        textDecoration: 'none',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }} onMouseEnter={(e) => {
                        e.target.style.color = primaryColor;
                        e.target.style.paddingLeft = '8px';
                      }} onMouseLeave={(e) => {
                        e.target.style.color = 'rgba(255,255,255,0.7)';
                        e.target.style.paddingLeft = '0';
                      }}>→ About Us</a>
                    )}
                    {sections.services && (
                      <a href="#services" style={{
                        color: 'rgba(255,255,255,0.7)',
                        textDecoration: 'none',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease'
                      }} onMouseEnter={(e) => {
                        e.target.style.color = primaryColor;
                        e.target.style.paddingLeft = '8px';
                      }} onMouseLeave={(e) => {
                        e.target.style.color = 'rgba(255,255,255,0.7)';
                        e.target.style.paddingLeft = '0';
                      }}>→ Our Services</a>
                    )}
                    <a href="#features" style={{
                      color: 'rgba(255,255,255,0.7)',
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      transition: 'all 0.3s ease'
                    }} onMouseEnter={(e) => {
                      e.target.style.color = primaryColor;
                      e.target.style.paddingLeft = '8px';
                    }} onMouseLeave={(e) => {
                      e.target.style.color = 'rgba(255,255,255,0.7)';
                      e.target.style.paddingLeft = '0';
                    }}>→ Why Choose Us</a>
                    <a href="#team" style={{
                      color: 'rgba(255,255,255,0.7)',
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      transition: 'all 0.3s ease'
                    }} onMouseEnter={(e) => {
                      e.target.style.color = primaryColor;
                      e.target.style.paddingLeft = '8px';
                    }} onMouseLeave={(e) => {
                      e.target.style.color = 'rgba(255,255,255,0.7)';
                      e.target.style.paddingLeft = '0';
                    }}>→ Book Appointment</a>
                  </div>
                </div>
                
                {/* Contact Info */}
                <div>
                  <h4 style={{
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    marginBottom: '25px',
                    color: 'white'
                  }}>Contact Us</h4>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '18px'}}>
                    {company.phone && (
                      <a href={`tel:${company.phone}`} style={{
                        color: 'rgba(255,255,255,0.7)',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease'
                      }} onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                         onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}>
                        <span style={{
                          width: '40px',
                          height: '40px',
                          background: `${primaryColor}20`,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.1rem'
                        }}>📞</span>
                        {company.phone}
                      </a>
                    )}
                    {company.email && (
                      <a href={`mailto:${company.email}`} style={{
                        color: 'rgba(255,255,255,0.7)',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease'
                      }} onMouseEnter={(e) => e.currentTarget.style.color = primaryColor}
                         onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}>
                        <span style={{
                          width: '40px',
                          height: '40px',
                          background: `${primaryColor}20`,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.1rem'
                        }}>✉️</span>
                        {company.email}
                      </a>
                    )}
                    {company.address && (
                      <div style={{
                        color: 'rgba(255,255,255,0.7)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        fontSize: '0.95rem'
                      }}>
                        <span style={{
                          width: '40px',
                          height: '40px',
                          background: `${primaryColor}20`,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.1rem',
                          flexShrink: 0
                        }}>📍</span>
                        <span style={{lineHeight: '1.6'}}>{company.address}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Footer Bottom */}
              <div style={{
                padding: '30px 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '20px'
              }}>
                <p style={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '0.95rem',
                  margin: 0,
                  fontWeight: '500'
                }}>
                  {company.footerText || `© ${new Date().getFullYear()} ${company.name}. All rights reserved.`}
                </p>
                <div style={{
                  display: 'flex',
                  gap: '24px'
                }}>
                  <span style={{color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem'}}>Privacy Policy</span>
                  <span style={{color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem'}}>Terms of Service</span>
                </div>
              </div>
            </div>
          </footer>
        </main>
      ) : (
        /* ========== BOOKING FORM PAGE ========== */
        <main className="st-booking-page">
          <div className="st-booking-header">
            <div className="st-container">
              <button 
                className="st-back-btn"
                onClick={() => {
                  setShowBookingForm(false);
                  setSelectedPreparer(null);
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
                </svg>
                <span>Back</span>
              </button>
            </div>
          </div>

          <div className="st-container">
            <div className="st-booking-content">
              <div className="st-booking-info">
                <h1>Book Your Appointment</h1>
                <div className="st-booking-preparer">
                  {selectedPreparer?.photo ? (
                    <img src={selectedPreparer.photo} alt={selectedPreparer.name} />
                  ) : (
                    <div className="st-booking-preparer-placeholder">👤</div>
                  )}
                  <div>
                    <h3>{selectedPreparer?.name}</h3>
                    <p>Tax Preparer</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="st-booking-form" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Personal Information */}
                <div className="st-form-section">
                  <div className="st-form-section-header">
                    <div className="st-form-step-number">1</div>
                    <h3>Personal Information</h3>
                  </div>
                  <div className="st-form-grid">
                    <div className="st-form-group">
                      <label>Full Name <span className="required">*</span></label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    <div className="st-form-group">
                      <label>Email <span className="required">*</span></label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                    <div className="st-form-group st-form-group-full">
                      <label>Phone Number <span className="required">*</span></label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="717-343-3822"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Service Details */}
                <div className="st-form-section">
                  <div className="st-form-section-header">
                    <div className="st-form-step-number">2</div>
                    <h3>Appointment Details</h3>
                  </div>
                  <div className="st-form-grid">
                    <div className="st-form-group">
                      <label>Number of Files <span className="required">*</span></label>
                      <input
                        type="number"
                        name="numberOfClients"
                        value={formData.numberOfClients}
                        onChange={handleInputChange}
                        min="1"
                        max="5"
                        required
                      />
                    </div>
                    <div className="st-form-group">
                      <label>Service Type <span className="required">*</span></label>
                      <select
                        name="fillingType"
                        value={formData.fillingType}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select service</option>
                        <option value="Individual Tax Return">Individual Tax Return</option>
                        <option value="Business Tax Return">Business Tax Return</option>
                        <option value="GST/BAS Return">GST/BAS Return</option>
                        <option value="Company Tax Return">Company Tax Return</option>
                        <option value="Trust Tax Return">Trust Tax Return</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Date & Time */}
                <div className="st-form-section">
                  <div className="st-form-section-header">
                    <div className="st-form-step-number">3</div>
                    <h3>Select Date & Time</h3>
                  </div>
                  
                  <button
                    type="button"
                    className={`st-date-trigger ${formData.date ? 'has-value' : ''}`}
                    onClick={() => setShowDatePopover(!showDatePopover)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    <span>
                      {(() => {
                        const numClients = parseInt(formData.numberOfClients) || 1;
                        const slots = formData.timeSlots || [];
                        if (slots.length === 0) return 'Select date and time';
                        if (numClients === 1 && slots.length === 1) {
                          return `${new Date(slots[0].date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at ${convert24to12Hour(slots[0].time)}`;
                        }
                        return `${slots.length}/${numClients} time slot(s) selected`;
                      })()}
                    </span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>

                  {showDatePopover && (
                    <div className="st-date-popover">
                      <div className="st-date-popover-header">
                        <h4>📅 Choose Your Appointment</h4>
                        <button type="button" className="st-popover-close" onClick={() => setShowDatePopover(false)}>×</button>
                      </div>
                      
                      <div className="st-date-popover-body">
                        {/* Calendar */}
                        <div className="st-calendar">
                          <div className="st-calendar-header">
                            <button 
                              type="button"
                              onClick={() => {
                                const prev = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1);
                                if (prev >= new Date(new Date().getFullYear(), new Date().getMonth(), 1)) {
                                  setCalendarMonth(prev);
                                }
                              }}
                            >‹</button>
                            <span>{calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                            <button 
                              type="button"
                              onClick={() => {
                                const next = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1);
                                setCalendarMonth(next);
                              }}
                            >›</button>
                          </div>
                          <div className="st-calendar-weekdays">
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                              <div key={d}>{d}</div>
                            ))}
                          </div>
                          <div className="st-calendar-days">
                            {(() => {
                              const firstDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
                              const lastDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
                              const startPadding = firstDay.getDay();
                              const days = [];
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const availableDates = getAvailableDates(selectedPreparer?.name);
                              
                              for (let i = 0; i < startPadding; i++) {
                                days.push(<div key={`empty-${i}`} className="st-cal-day empty"></div>);
                              }
                              
                              for (let d = 1; d <= lastDay.getDate(); d++) {
                                const dateObj = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), d);
                                const dateStr = dateObj.toISOString().split('T')[0];
                                const isPast = dateObj < today;
                                const hasAvailability = availableDates.includes(dateStr);
                                const fullyBooked = hasAvailability && isDateFullyBooked(dateStr, selectedPreparer?.name);
                                const isSelected = formData.date === dateStr;
                                const hasSlotOnDate = (formData.timeSlots || []).some(s => s.date === dateStr);
                                
                                days.push(
                                  <button
                                    key={d}
                                    type="button"
                                    className={`st-cal-day ${isSelected ? 'selected' : ''} ${hasSlotOnDate && !isSelected ? 'has-slot' : ''} ${isPast ? 'past' : ''} ${!hasAvailability ? 'unavailable' : ''} ${fullyBooked ? 'booked' : ''} ${hasAvailability && !fullyBooked && !isPast ? 'available' : ''}`}
                                    onClick={() => {
                                      if (!isPast && hasAvailability && !fullyBooked) {
                                        setFormData(prev => ({ ...prev, date: dateStr }));
                                      }
                                    }}
                                    disabled={isPast || !hasAvailability || fullyBooked}
                                  >
                                    {d}
                                  </button>
                                );
                              }
                              return days;
                            })()}
                          </div>
                        </div>

                        {/* Time Slots */}
                        <div className="st-time-slots">
                          <h4>⏰ Available Times {(() => {
                            const numClients = parseInt(formData.numberOfClients) || 1;
                            const slots = formData.timeSlots || [];
                            return numClients > 1 ? <span style={{fontSize: '0.85rem', fontWeight: '400', color: '#666'}}>({slots.length}/{numClients} selected)</span> : null;
                          })()}</h4>
                          {!formData.date ? (
                            <div className="st-time-placeholder">
                              <p>Select a date first</p>
                            </div>
                          ) : (
                            <div className="st-time-grid">
                              {getAvailableTimeSlots(formData.date, selectedPreparer?.name).length > 0 ? (
                                getAvailableTimeSlots(formData.date, selectedPreparer?.name).map((slot, index) => {
                                  const numClients = parseInt(formData.numberOfClients) || 1;
                                  const isSelected = (formData.timeSlots || []).some(s => s.date === formData.date && s.time === slot);
                                  const isFull = (formData.timeSlots || []).length >= numClients && !isSelected;
                                  
                                  return (
                                    <button
                                      key={index}
                                      type="button"
                                      className={`st-time-btn ${isSelected ? 'selected' : ''}`}
                                      style={isFull ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                      onClick={() => {
                                        if (!isFull || isSelected) {
                                          handleTimeSlotToggle(formData.date, slot);
                                        }
                                      }}
                                    >
                                      {convert24to12Hour(slot)} {isSelected ? '✓' : ''}
                                    </button>
                                  );
                                })
                              ) : (
                                <div className="st-time-placeholder">
                                  <p style={{ color: '#ef4444', fontWeight: '500' }}>No times available at this moment</p>
                                </div>
                              )}
                            </div>
                          )}
                          {/* Show selected slots summary for multi-client */}
                          {(() => {
                            const numClients = parseInt(formData.numberOfClients) || 1;
                            const slots = formData.timeSlots || [];
                            if (numClients > 1 && slots.length > 0) {
                              return (
                                <div style={{ marginTop: '12px', padding: '10px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                                  <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#0369a1', marginBottom: '6px' }}>Selected Slots:</div>
                                  {slots.map((s, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', padding: '4px 0', color: '#334155' }}>
                                      <span>Client {i + 1}: {new Date(s.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {convert24to12Hour(s.time)}</span>
                                      <button type="button" onClick={() => handleTimeSlotToggle(s.date, s.time)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1rem', padding: '0 4px' }}>×</button>
                                    </div>
                                  ))}
                                  {slots.length < numClients && <div style={{ fontSize: '0.8rem', color: '#f59e0b', marginTop: '6px' }}>⚠ Select {numClients - slots.length} more time slot(s)</div>}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                      
                      <div className="st-date-popover-footer">
                        <button type="button" className="st-btn st-btn-outline st-btn-sm" onClick={() => setFormData(prev => ({ ...prev, date: '', time: '', timeSlots: [] }))}>
                          Clear
                        </button>
                        <button type="button" className="st-btn st-btn-primary st-btn-sm" onClick={() => setShowDatePopover(false)}>
                          Done
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Appointment Summary - Shows selected date & time clearly */}
                {(formData.timeSlots && formData.timeSlots.length > 0) && (
                  <div style={{
                    background: `linear-gradient(135deg, ${primaryColor}15 0%, ${secondaryColor}15 100%)`,
                    border: `2px solid ${primaryColor}`,
                    borderRadius: 'clamp(12px, 3vw, 16px)',
                    padding: 'clamp(15px, 4vw, 20px)',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'clamp(8px, 2vw, 10px)',
                      marginBottom: 'clamp(12px, 3vw, 15px)'
                    }}>
                      <span style={{fontSize: 'clamp(1.2rem, 4vw, 1.5rem)'}}>✅</span>
                      <h4 style={{
                        margin: 0,
                        color: primaryColor,
                        fontSize: 'clamp(0.95rem, 3vw, 1.1rem)',
                        fontWeight: '700'
                      }}>Your Appointment Summary {parseInt(formData.numberOfClients) > 1 ? `(${formData.timeSlots.length} client(s))` : ''}</h4>
                    </div>
                    
                    <div style={{
                      display: 'grid',
                      gap: 'clamp(8px, 2vw, 12px)'
                    }}>
                      {/* Show each time slot */}
                      {formData.timeSlots.map((slot, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'clamp(8px, 2vw, 12px)',
                          background: 'white',
                          padding: 'clamp(10px, 2.5vw, 12px) clamp(12px, 3vw, 16px)',
                          borderRadius: 'clamp(8px, 2vw, 10px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}>
                          <span style={{
                            fontSize: 'clamp(1.3rem, 4vw, 1.8rem)',
                            width: 'clamp(36px, 10vw, 45px)',
                            height: 'clamp(36px, 10vw, 45px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: `${primaryColor}20`,
                            borderRadius: 'clamp(8px, 2vw, 10px)',
                            flexShrink: 0
                          }}>{parseInt(formData.numberOfClients) > 1 ? `#${idx + 1}` : '📅'}</span>
                          <div style={{minWidth: 0, flex: 1}}>
                            <div style={{fontSize: 'clamp(0.7rem, 2vw, 0.8rem)', color: '#64748b', fontWeight: '500'}}>
                              {parseInt(formData.numberOfClients) > 1 ? `CLIENT ${idx + 1}` : 'DATE & TIME'}
                            </div>
                            <div style={{fontSize: 'clamp(0.9rem, 2.8vw, 1.1rem)', color: '#1a1a2e', fontWeight: '700', wordBreak: 'break-word'}}>
                              {new Date(slot.date + 'T00:00:00').toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                month: 'short', 
                                day: 'numeric'
                              })} at {convert24to12Hour(slot.time)}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {/* Preparer */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'clamp(8px, 2vw, 12px)',
                        background: 'white',
                        padding: 'clamp(10px, 2.5vw, 12px) clamp(12px, 3vw, 16px)',
                        borderRadius: 'clamp(8px, 2vw, 10px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                      }}>
                        <span style={{
                          fontSize: 'clamp(1.3rem, 4vw, 1.8rem)',
                          width: 'clamp(36px, 10vw, 45px)',
                          height: 'clamp(36px, 10vw, 45px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `${primaryColor}20`,
                          borderRadius: 'clamp(8px, 2vw, 10px)',
                          flexShrink: 0
                        }}>👤</span>
                        <div style={{minWidth: 0, flex: 1}}>
                          <div style={{fontSize: 'clamp(0.7rem, 2vw, 0.8rem)', color: '#64748b', fontWeight: '500'}}>TAX PREPARER</div>
                          <div style={{fontSize: 'clamp(0.9rem, 2.8vw, 1.1rem)', color: '#1a1a2e', fontWeight: '700', wordBreak: 'break-word'}}>
                            {selectedPreparer?.name || 'Not selected'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit */}
                <button type="submit" className="st-btn st-btn-primary st-btn-lg st-btn-block" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="st-spinner"></span>
                      <span>Booking...</span>
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      <span>Book Appointment</span>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </main>
      )}

      {/* ========== LOGIN MODAL - MODERN DESIGN ========== */}
      {showLoginModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
          paddingTop: isPWAMode ? 'max(20px, calc(env(safe-area-inset-top, 0px) + 20px))' : '20px'
        }} onClick={() => setShowLoginModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '440px',
            maxHeight: isPWAMode ? 'calc(100vh - env(safe-area-inset-top, 0px) - 40px)' : 'calc(100vh - 40px)',
            overflow: 'auto',
            boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
            animation: 'modalSlideIn 0.3s ease'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div style={{
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
              padding: 'clamp(24px, 5vw, 40px) clamp(20px, 4vw, 30px) clamp(20px, 4vw, 30px)',
              textAlign: 'center',
              position: 'relative'
            }}>
              <button 
                onClick={() => setShowLoginModal(false)}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  width: '44px',
                  height: '44px',
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  color: 'white',
                  fontSize: '1.3rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
              >×</button>
              
              <div style={{
                width: '70px',
                height: '70px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '2rem'
              }}>🔐</div>
              <h2 style={{
                color: 'white',
                fontSize: '1.6rem',
                fontWeight: '700',
                marginBottom: '8px'
              }}>👤 Preparer Login</h2>
              <p style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.95rem'
              }}>Access your dashboard to manage appointments</p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleLogin} style={{padding: 'clamp(20px, 4vw, 30px)'}}>
              <div style={{marginBottom: '20px'}}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.95rem'
                }}>Username</label>
                <div style={{position: 'relative'}}>
                  <span style={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '1.1rem'
                  }}>👤</span>
                  <input
                    type="text"
                    value={loginData.username}
                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                    placeholder="Preparer username"
                    required
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 45px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = primaryColor}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              <div style={{marginBottom: '25px'}}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '0.95rem'
                }}>Password</label>
                <div style={{position: 'relative'}}>
                  <span style={{
                    position: 'absolute',
                    left: '14px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '1.1rem'
                  }}>🔒</span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    placeholder="Enter your password"
                    required
                    style={{
                      width: '100%',
                      padding: '14px 45px 14px 45px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = primaryColor}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      fontSize: '1.1rem',
                      cursor: 'pointer',
                      padding: '4px'
                    }}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                disabled={loginLoading}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1.05rem',
                  fontWeight: '700',
                  cursor: loginLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  opacity: loginLoading ? 0.7 : 1,
                  boxShadow: `0 4px 15px ${primaryColor}40`
                }}
                onMouseEnter={(e) => {
                  if (!loginLoading) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 6px 20px ${primaryColor}50`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 15px ${primaryColor}40`;
                }}
              >
                {loginLoading ? (
                  <>
                    <span style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: 'white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></span>
                    <span>Logging in...</span>
                  </>
                ) : (
                  <>
                    <span>🔓</span>
                    <span>Login to Dashboard</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
      
      {/* ========== PWA INSTALL INSTRUCTIONS MODAL ========== */}
      {showInstallModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 1001
        }} onClick={() => setShowInstallModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '420px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            animation: 'modalSlideIn 0.3s ease'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{
              background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
              padding: '24px 20px',
              textAlign: 'center',
              position: 'relative'
            }}>
              <button
                onClick={() => setShowInstallModal(false)}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
              >×</button>
              
              <div style={{
                width: '70px',
                height: '70px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '2rem'
              }}>📲</div>
              <h2 style={{
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '8px'
              }}>Add to Home Screen</h2>
              <p style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.95rem'
              }}>Install {company?.name || 'TaxFilePro'} as an app for quick access</p>
            </div>

            {/* Instructions */}
            <div style={{padding: '24px 20px'}}>
              {/* iOS Instructions */}
              <div style={{
                background: '#f0f9ff',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px'}}>
                  <span style={{fontSize: '1.5rem'}}>🍎</span>
                  <span style={{fontWeight: '600', color: '#0369a1'}}>iPhone / iPad (Safari)</span>
                </div>
                <ol style={{margin: 0, paddingLeft: '20px', color: '#374151', lineHeight: '1.8', fontSize: '0.9rem'}}>
                  <li>Tap the <strong>Share</strong> button <span style={{background: '#e0f2fe', padding: '2px 6px', borderRadius: '4px'}}>⬆️</span> at the bottom</li>
                  <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                  <li>Tap <strong>"Add"</strong> in the top right corner</li>
                </ol>
              </div>
              
              {/* Android Instructions */}
              <div style={{
                background: '#f0fdf4',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px'}}>
                  <span style={{fontSize: '1.5rem'}}>🤖</span>
                  <span style={{fontWeight: '600', color: '#15803d'}}>Android (Chrome)</span>
                </div>
                <ol style={{margin: 0, paddingLeft: '20px', color: '#374151', lineHeight: '1.8', fontSize: '0.9rem'}}>
                  <li>Tap the <strong>menu</strong> button <span style={{background: '#dcfce7', padding: '2px 6px', borderRadius: '4px'}}>⋮</span> (3 dots)</li>
                  <li>Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></li>
                  <li>Tap <strong>"Add"</strong> to confirm</li>
                </ol>
              </div>
              
              {/* Benefits */}
              <div style={{
                marginTop: '20px',
                padding: '16px',
                background: '#fefce8',
                borderRadius: '12px',
                border: '1px solid #fef08a'
              }}>
                <p style={{fontWeight: '600', color: '#854d0e', marginBottom: '10px', fontSize: '0.95rem'}}>✨ Benefits of Installing:</p>
                <ul style={{margin: 0, paddingLeft: '20px', color: '#713f12', fontSize: '0.9rem', lineHeight: '1.7'}}>
                  <li>Quick access from your home screen</li>
                  <li>Opens directly to this company's page</li>
                  <li>Works like a native app</li>
                  <li>Easy preparer login access</li>
                </ul>
              </div>
              
              {/* Close Button */}
              <button
                onClick={() => setShowInstallModal(false)}
                style={{
                  width: '100%',
                  marginTop: '20px',
                  padding: '14px',
                  background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyLandingPage;
